import { slugify } from "./countries";

export interface FocusAreaEntry {
  name: string;
  slug: string;
  /** Optional list of label variants to match against investor focus_areas (case-insensitive). */
  aliases?: string[];
  /** Optional override for the free-text term used in description ILIKE queries. Defaults to `name`. */
  searchTerm?: string;
}

interface RawEntry {
  name: string;
  slug?: string;
  aliases?: string[];
  searchTerm?: string;
}

const RAW: Array<string | [string, string[]] | RawEntry> = [
  "AI",
  "Adtech",
  "Advanced manufacturing",
  "Aerospace",
  "Agtech",
  "App",
  "Art",
  "Automotive",
  "Aviation",
  "B2B",
  "B2C",
  "Beauty",
  "Big data",
  "Biotech",
  "CPG",
  "Cannabis",
  "Cleantech",
  ["Climate Tech", ["Climate Tech", "ClimateTech", "Climate"]],
  "Cloud",
  "Collaboration Tool",
  "Community",
  "Construction",
  "Creator Economy",
  "Customer Experience",
  "Cybersecurity",
  "D2C",
  "Data infrastructure",
  "DeFi",
  "Deeptech",
  "Defensetech",
  "Delivery",
  "DevOps",
  "Developer Tools",
  "Drone",
  "E-commerce",
  "ESG",
  "Edtech",
  "Energy",
  "Engineering",
  "Entertainment",
  "Esports",
  "Fashion",
  "Female founders",
  "Femtech",
  "Fintech",
  "Fitness",
  ["Food & Beverage", ["Food & Beverage", "Food and Beverage", "F&B"]],
  "Foodtech",
  "Future of Work",
  "Gaming",
  "Genomics",
  "GovTech",
  "HRtech",
  "Hardware",
  "Healthtech",
  "IP",
  "Industry 4.0",
  "Industry Agnostic",
  "Insurtech",
  "IoT",
  "LGBT",
  "LMS",
  "LegalTech",
  "Life sciences",
  "Logistics",
  "Luxury",
  "Manufacturing",
  "Marketplace",
  "Martech",
  "Media",
  "Medical Device",
  "Medtech",
  "Mental Health",
  "Metaverse",
  ["Oil & Gas", ["Oil & Gas", "Oil and Gas", "Oil, Gas", "Oil", "Gas"]],
  "Open-source",
  "Proptech",
  "Psychedelics",
  "Quantum computing",
  "Regtech",
  "Retail",
  "Robotics",
  "SaaS",
  "Salestech",
  "Semiconductors",
  "Silvertech",
  "Smart Cities",
  "Social Impact",
  "Social Media",
  "Space",
  "Sports",
  "Student",
  "Supply Chain",
  "Sustainability",
  "Tech",
  "Telcom",
  "Transportation",
  "Traveltech",
  "Underrepresented",
  "VR",
  "Vertical SaaS",
  "Vicetech",
  "Water",
  "Wealthtech",
  "Wearables",
  "Web Tool",
  "Web3",
  "Wellbeing",
  "Wellness",
  "XR",
  "enterprise",
  ["genAI", ["genAI", "Generative AI", "GenAI"]],
  // --- Expansion: emerging / thematic focus areas (description-ILIKE driven) ---
  { name: "AI Agents", searchTerm: "AI agents" },
  { name: "Agentic AI", searchTerm: "agentic AI" },
  { name: "LLM", searchTerm: "large language model" },
  { name: "Foundation Model", searchTerm: "foundation model" },
  { name: "AI Infrastructure", searchTerm: "AI infrastructure" },
  { name: "AI Safety", searchTerm: "AI safety" },
  { name: "Synthetic Data", searchTerm: "synthetic data" },
  { name: "Edge AI", searchTerm: "edge AI" },
  { name: "Multimodal AI", searchTerm: "multimodal" },
  { name: "Vertical AI", searchTerm: "vertical AI" },
  { name: "AI Automation", searchTerm: "AI automation" },
  { name: "AI Copilot", searchTerm: "AI copilot" },
  { name: "Embodied AI", searchTerm: "embodied AI" },
  { name: "Carbon Capture", searchTerm: "carbon capture" },
  { name: "Green Hydrogen", searchTerm: "green hydrogen" },
  { name: "Battery Tech", searchTerm: "battery" },
  { name: "Nuclear Energy", searchTerm: "nuclear" },
  { name: "Solar Tech", searchTerm: "solar" },
  { name: "Grid Tech", searchTerm: "grid technology" },
  { name: "Circular Economy", searchTerm: "circular economy" },
  { name: "Net Zero", searchTerm: "net zero" },
  { name: "Ocean Tech", searchTerm: "ocean" },
  { name: "EV Charging", searchTerm: "EV charging" },
  { name: "Energy Storage", searchTerm: "energy storage" },
  { name: "Longevity", searchTerm: "longevity" },
  { name: "Digital Health", searchTerm: "digital health" },
  { name: "Precision Medicine", searchTerm: "precision medicine" },
  { name: "Synthetic Biology", searchTerm: "synthetic biology" },
  { name: "Drug Discovery", searchTerm: "drug discovery" },
  { name: "Telemedicine", searchTerm: "telemedicine" },
  { name: "Diagnostics", searchTerm: "diagnostics" },
  { name: "Microbiome", searchTerm: "microbiome" },
  { name: "Embedded Finance", searchTerm: "embedded finance" },
  { name: "Open Banking", searchTerm: "open banking" },
  { name: "Crypto", searchTerm: "crypto" },
  { name: "Stablecoin", searchTerm: "stablecoin" },
  { name: "Real World Assets", slug: "rwa", searchTerm: "real world assets" },
  { name: "Payments", searchTerm: "payments" },
  { name: "Neobank", searchTerm: "neobank" },
  { name: "Treasury Tech", searchTerm: "treasury" },
  { name: "Dual Use Tech", searchTerm: "dual use" },
  { name: "Military AI", searchTerm: "military AI" },
  { name: "Cyber Defense", searchTerm: "cyber defense" },
  { name: "No-Code", slug: "no-code", searchTerm: "no-code" },
  { name: "Low-Code", slug: "low-code", searchTerm: "low-code" },
  { name: "Workforce Tech", searchTerm: "workforce" },
  { name: "Gig Economy", searchTerm: "gig economy" },
  { name: "Humanoid Robotics", searchTerm: "humanoid" },
  { name: "Warehouse Automation", searchTerm: "warehouse automation" },
  { name: "Autonomous Vehicles", searchTerm: "autonomous vehicle" },
  { name: "Satellite Tech", searchTerm: "satellite" },
  { name: "Geospatial Tech", searchTerm: "geospatial" },
  { name: "Social Commerce", searchTerm: "social commerce" },
  { name: "Recommerce", searchTerm: "recommerce" },
  { name: "Subscription Economy", searchTerm: "subscription" },
  { name: "API Economy", slug: "api-economy", searchTerm: "API-first" },
  { name: "MLOps", searchTerm: "MLOps" },
  { name: "Vector Database", searchTerm: "vector database" },
  { name: "AI Compute", searchTerm: "AI compute" },
  { name: "Post-Quantum Cryptography", searchTerm: "post-quantum" },
  { name: "Financial Inclusion", searchTerm: "financial inclusion" },
  { name: "Senior Care Tech", searchTerm: "senior care" },
  { name: "Mental Wellness", searchTerm: "mental wellness" },
  { name: "Housing Tech", searchTerm: "housing" },
  { name: "Alt Protein", searchTerm: "alternative protein" },
  { name: "Precision Fermentation", searchTerm: "precision fermentation" },
  { name: "Urban Farming", searchTerm: "urban farming" },
  { name: "Sleep Tech", searchTerm: "sleep" },
  { name: "Fertility Tech", searchTerm: "fertility" },
  { name: "Pet Tech", searchTerm: "pet tech" },
  { name: "National Security", searchTerm: "national security" },
  { name: "Critical Infrastructure Security", searchTerm: "critical infrastructure security" },
  { name: "Robotics & Defense", searchTerm: "defense robotics" },
  { name: "Network Security", searchTerm: "network security" },
  { name: "Surveillance", searchTerm: "surveillance" },
  { name: "Biometrics", searchTerm: "biometrics" },
  { name: "Unmanned Systems", searchTerm: "unmanned systems" },
  { name: "Homeland Security", searchTerm: "homeland security" },
  { name: "Autonomous Systems", searchTerm: "autonomous systems" },
];

export const FOCUS_AREAS: FocusAreaEntry[] = RAW.map((r) => {
  if (typeof r === "string") {
    return { name: r, slug: slugify(r), aliases: [r] };
  }
  if (Array.isArray(r)) {
    const [name, aliases] = r;
    return { name, slug: slugify(name), aliases };
  }
  return {
    name: r.name,
    slug: r.slug ?? slugify(r.name),
    aliases: r.aliases ?? [r.name],
    searchTerm: r.searchTerm,
  };
}).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

// Dev-time guard: detect slug collisions early
if (import.meta.env?.DEV) {
  const seen = new Set<string>();
  for (const f of FOCUS_AREAS) {
    if (seen.has(f.slug)) {
      // eslint-disable-next-line no-console
      console.error(`[focusAreas] Duplicate slug: ${f.slug}`);
    }
    seen.add(f.slug);
  }
}

export const FOCUS_AREAS_BY_SLUG: Record<string, FocusAreaEntry> = Object.fromEntries(
  FOCUS_AREAS.map((f) => [f.slug, f]),
);

export function focusAreaFromSlug(slug: string | undefined): FocusAreaEntry | null {
  if (!slug) return null;
  return FOCUS_AREAS_BY_SLUG[slug.toLowerCase()] ?? null;
}

/** Group focus areas alphabetically by their first character for the index page. */
export interface FocusGroup {
  letter: string;
  items: FocusAreaEntry[];
}

export const FOCUS_AREAS_BY_LETTER: FocusGroup[] = (() => {
  const map = new Map<string, FocusAreaEntry[]>();
  for (const f of FOCUS_AREAS) {
    const first = f.name[0].toUpperCase();
    const letter = /[A-Z]/.test(first) ? first : "#";
    if (!map.has(letter)) map.set(letter, []);
    map.get(letter)!.push(f);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([letter, items]) => ({ letter, items }));
})();

/** Test if an investor's focus_areas array matches the given focus entry. */
export function investorMatchesFocus(
  investorFocusAreas: string[] | null | undefined,
  focus: FocusAreaEntry,
): boolean {
  if (!investorFocusAreas?.length) return false;
  const aliases = (focus.aliases ?? [focus.name]).map((a) => a.toLowerCase().trim());
  return investorFocusAreas.some((v) => {
    const k = (v ?? "").toLowerCase().trim();
    if (!k) return false;
    return aliases.some((a) => k === a || k.includes(a) || a.includes(k));
  });
}
